import { useCallback } from 'react';
import { FaxHooks, FaxV1 } from '@frontend/api-fax';
import { useTranslation } from '@frontend/i18n';
import { useFaxNotification } from '@frontend/notifications';
import { GetWeavePopNotificationByType } from '@frontend/types';
import { FaxEventPayload, GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

// Type override for action method fax event. To be removed when backend support location_id in this event's payload params.
type FaxEventParamsOverride = FaxEventPayload['params'] & { locationID: string };

export const useFaxEventSubscription = () => {
  const { create } = useFaxNotification();
  const { t } = useTranslation('notifications');
  const { addToListFaxCache, updateFaxInListFaxCache } = FaxV1.FaxQueryUpdaters.useListFaxQueryUpdater();
  const invalidateFaxCount = FaxHooks.useInvalidateFaxCount();
  const invalidateFaxSearch = FaxV1.FaxQueryUpdaters.useInvalidateFaxSearch();

  const createInboundFaxNotification = useCallback(
    (id: string, params: FaxEventPayload['params']) => {
      const notification: GetWeavePopNotificationByType<'fax-new'> = {
        id,
        timestamp: Date.now(),
        type: 'fax-new',
        payload: {
          faxId: params.fax_id,
          action: params.action,
          from: params.from,
          to: params.to,
        },
        state: {
          paused: false,
          status: 'unread',
        },
      };

      create(notification, {
        os: {
          title: t('Fax'),
          body: params.from,
        },
      });
    },
    [create, t]
  );

  const handler: GetWebsocketEventHandler<'FaxNotification'> = useCallback(
    (payload) => {
      const { params } = payload;

      if (params.action === 'inbound') {
        const locationId = params.location_id || (params as FaxEventParamsOverride).locationID;
        addToListFaxCache({ faxId: params.fax_id, locationId });
        createInboundFaxNotification(payload.id, params);
        invalidateFaxCount();
        invalidateFaxSearch();
      }

      if (params.action === 'status') {
        const locationId = params.location_id || (params as FaxEventParamsOverride).locationID;
        updateFaxInListFaxCache({
          faxId: params.fax_id,
          locationId,
        });
        invalidateFaxSearch();
      }
    },
    [addToListFaxCache, createInboundFaxNotification, updateFaxInListFaxCache]
  );

  return useWebsocketEventSubscription('FaxNotification', handler);
};
